import { Component, Fragment, h, Prop, State } from '@stencil/core';
import { itemDescriptionKey, itemKey, itemTitleKey } from '../../../../utils/trans';
import { ButtonAppearance, ButtonControlValues, ButtonSize, ButtonStyle } from '../../rentware-button/types';
import { AmountControlValues, ComponentView, ShortArticle, SelectStyle, ShortPrice } from '../../../../types/types';
import { ComponentStatus } from '../../../rentware-search/utils';
import { t } from '../../../../redux/utils';

@Component({
  tag: 'rtr-short-article-list',
  styleUrl: 'style.sass',
  shadow: false,
})
export class RtrShortArticleList {
  @Prop() article: ShortArticle;
  @Prop() componentStatus: ComponentStatus;
  @Prop() isWaiting: boolean = false;
  @Prop() onButtonClick: () => void;
  @Prop() onNotAvailableButtonClick: () => void;
  @Prop() onRemoveArticle: () => void;
  @Prop() onArticlesLoaded: () => void = () => {};
  @Prop() onChangeAmountToBook: (newAmount: number) => void;
  @Prop() onChangeAmount: (newAmount: number, oldAmount: number) => void;
  @Prop() onClickForDetails: (articleId: string) => void;
  @Prop() onVariationChange: (variationId: string) => void;
  @Prop() onMileageChange: (optionId: string) => void;

  @State() articleWasVisible = false;
  articleContainer: HTMLDivElement;

  componentDidLoad() {
    this.onArticlesLoaded?.();

    let observer = new IntersectionObserver(this.onVisible);
    observer.observe(this.articleContainer);
  }

  onVisible = entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting && !this.articleWasVisible) {
        this.articleWasVisible = true;
      }
    });
  };

  get buttonText(): string {
    return t('checkout.addToCart');
  }

  get checkAvailabilityText(): string {
    return t('search.checkAvailability');
  }

  get selectedText(): string {
    return t('checkout.articleSelected');
  }

  get notAvailableText(): string {
    if (this.componentStatus === ComponentStatus.Ready) {
      return t('search.notAvailable');
    } else {
      return t(`search.${this.componentStatus}`);
    }
  }

  get showArticlePrice() {
    return this.componentStatus === ComponentStatus.Ready && this.article.calculatedQuantity > 0;
  }

  get showArticleQty() {
    return this.componentStatus === ComponentStatus.Ready && this.article.calculatedQuantity > 1;
  }

  get showCheckAvailability() {
    return this.componentStatus === ComponentStatus.Ready && this.article.calculatedQuantity < 1;
  }

  get mileagePrice(): ShortPrice {
    return this.article?.mileageOptions?.find(item => item.id === this.article.selectedMileageId)?.price;
  }

  get showWaitingSpinnerTemplate() {
    return this.article.isWaiting && <rentware-spinner withOverlay={true} />;
  }

  get articleHasMileageOrVariations(): boolean {
    return !!(this.article?.mileageOptions || this.article?.variation);
  }

  get showMileageTemplate() {
    if (this.article?.mileageOptions) {
      return (
        <rentware-select-mileage
          mileageOptions={this.article.mileageOptions}
          available={this.article.available}
          selectStyle={SelectStyle.Default}
          selectedMileageId={this.article.selectedMileageId}
          onMileageChange={this.onMileageChange}
          isWaiting={this.article.isWaiting}
        />
      );
    }
  }

  get additionalActionClass(): string {
    if (this.article.mileageOptions && this.article.variation) {
      return 'AdditionalActionContainer ChildHalfWidth';
    }

    return 'AdditionalActionContainer';
  }

  get showVariationTemplate() {
    if (this.article?.variation) {
      return (
        <rentware-select
          selectStyle={SelectStyle.Default}
          label={this.article.variation.name}
          value={this.article.selectedVariationId}
          onValueChange={e => {
            this.onVariationChange(e.detail.value);
          }}
          options={this.article.variation.items.map(item => {
            return {
              label: item.name,
              value: item.id,
              disabled: !item.maxQuantity && !item.quantityInCart,
            };
          })}
        />
      );
    }
  }

  get showControlButtonTemplate() {
    const inCartControlValues: AmountControlValues = {
      text: this.selectedText,
      minAmount: this.article.minQuantity,
      maxAmount: this.article.calculatedMaxQuantity,
      itemAmount: this.article.calculatedQuantity,
      changeAmountCallback: this.onChangeAmount,
      deleteArticleCallback: this.onRemoveArticle,
      disabled: this.article.isWaiting,
    };

    const notInCartControlValues: ButtonControlValues = {
      id: this.article?.id,
      minQuantity: this.article.minQuantity,
      maxQuantity: this.article.calculatedMaxQuantity,
      currentQuantity: this.article.calculatedQuantity,
      onChange: this.onChangeAmountToBook,
    };

    if (this.article.hasSelectedVariationInCart || (!this.article.variation && this.article.quantityInCart > 0)) {
      return <rentware-amount-control view={ComponentView.Item} controlValues={inCartControlValues} />;
    }

    if (!this.article.available) {
      const buttonAppearance: ButtonAppearance = {
        style: ButtonStyle.NotAvailable,
        size: ButtonSize.L,
      };

      if (this.componentStatus !== ComponentStatus.Ready) {
        buttonAppearance.style = ButtonStyle.Inverse;
      }

      return <rentware-button buttonId="ListArticleNotAvailableButton" appearance={buttonAppearance} buttonText={this.notAvailableText} onClick={this.onNotAvailableButtonClick} />;
    }

    return <rentware-button buttonText={this.buttonText} onClick={this.onButtonClick} controlValues={notInCartControlValues} />;
  }

  get articleInfoStripped() {
    const unstripped = t(itemDescriptionKey(this.article.id));
    const div = document.createElement('div');
    div.innerHTML = unstripped;
    return div.textContent || div.innerText || '';
  }

  render() {
    return (
      <Fragment>
        {this.showWaitingSpinnerTemplate}

        <div ref={el => (this.articleContainer = el)} class="ArticleListContainer">
          {this.articleWasVisible && (
            <Fragment>
              <div class="ArticleContainerImageContent">
                <div class="ArticleContainerImage">
                  <rentware-image
                    className="ArticleImg"
                    placeholderH={50}
                    placeholderW={50}
                    src={this.article.image}
                    autoSize={true}
                    onClick={() => this.onClickForDetails(this.article.id)}
                  />
                </div>

                <div class="ArticleContainerContent">
                  <div class="Title" onClick={() => this.onClickForDetails(this.article.id)}>
                    {t(itemKey(this.article.id))}
                  </div>

                  <div class="UnderTitle" onClick={() => this.onClickForDetails(this.article.id)}>
                    {t(itemTitleKey(this.article.id))}
                  </div>

                  <rentware-spacer size="S" />

                  <div class="Description">{this.articleInfoStripped}</div>

                  {this.articleHasMileageOrVariations && <rentware-spacer size="M" />}

                  <div class={this.additionalActionClass}>
                    {this.showVariationTemplate}

                    {this.showMileageTemplate}
                  </div>
                </div>
              </div>

              <div class="ActionContainer">
                <div class="ActionInfoContainer">
                  {this.article.quantityInCart > 0 && <rentware-expiration-timer />}

                  <div class="PriceContainer">
                    {this.showArticleQty && <span>{this.article.calculatedQuantity}x&nbsp;</span>}

                    {this.showArticlePrice && <rentware-item-price price={this.article.price} mileagePrice={this.mileagePrice}  />}

                    {this.showCheckAvailability && (
                      <span class="AvailabilityLink" onClick={() => this.onClickForDetails(this.article.id)}>
                        {this.checkAvailabilityText}
                      </span>
                    )}
                  </div>
                </div>

                {this.showControlButtonTemplate}
              </div>
            </Fragment>
          )}
        </div>

        <rentware-spacer size="S" />

        {!this.article.lastArticle && <rentware-divider />}

        {this.article.lastArticle && <rentware-spacer size="M" />}
      </Fragment>
    );
  }
}
